/* Copyright (c) 2020-present CloudIO Inc */

import { isValidButtonColor } from 'componentregistry/components/Button/Button';
import { toolTipProps } from 'componentregistry/utils/commonProps';
import { Platform } from '../../../../platform/Platform';
import {
  ComponentManager,
  ComponentManagerFn,
  RequiredItem,
} from '../../../../registry/Component.type';
import { ItemType } from '../../../../registry/ComponentRegistry';
import { baseComponentManager } from '../../../../registry/Components';
import { buttonSize, buttonVariant } from '../../../../registry/constants';
import { baseColumnProps, isBaseColumnPropertyVisible } from '../BaseColumn';
import { defaultValue } from './ButtonColumn';
import { helpRenders } from './ButtonColumn_Help';

const name = 'Button Column';

const ButtonColumnManager: ComponentManagerFn<'ButtonColumn'> = (
  platform: Platform,
): ComponentManager<'ButtonColumn'> => {
  return {
    ...baseComponentManager<'ButtonColumn'>(platform, 'ButtonColumn'),
    type: 'ButtonColumn',
    name,
    isChildAllowed: (page, item, childType) =>
      (['TriggerOnClick'] as ItemType[]).includes(childType),
    defaultValue,
    props: {
      ...baseColumnProps,
      viewAttribute: {
        ...baseColumnProps.viewAttribute,
        required: false,
      },
      color: {
        label: 'Color',
        type: 'ThemeBGColor',
        filterColor: isValidButtonColor,
      },
      disableCondition: {
        label: 'Disable Condition',
        type: 'expressionOnly',
        returnType: () => 'boolean',
      },
      fullWidth: {
        label: 'Full Width',
        type: 'boolean',
        helpText:
          'If checked, the button will take up the full width of its container.',
      },
      size: {
        label: 'Size',
        type: 'select',
        options: buttonSize,
      },
      startIcon: {
        label: 'Start Icon',
        type: 'iconselect',
        helpText: 'Icon to be placed before the text.',
        parentColorProp: 'color',
      },
      endIcon: {
        label: 'End Icon',
        type: 'iconselect',
        helpText: 'Icon to be placed after the text.',
        parentColorProp: 'color',
      },
      iconColor: {
        label: 'Icon Color',
        type: 'ColorExpression',
        helpText:
          'e.g. background.paper or background.default or primary.main or primary.dark or primary.light or any valid CSS color string',
      },
      text: {
        label: 'Text',
        type: 'expression',
        returnType: async () => 'string',
      },
      hotKey: {
        label: 'Hot Key',
        type: 'string',
        maxLength: 1,
        helpText: (item: RequiredItem<'ButtonColumn'>) => {
          const key =
            item.props.hotKey ||
            (item.props.text.type === 'text' && item.props.text.value?.length
              ? item.props.text.value.charAt(0)
              : '<Hot Key>');

          return `A shortcut key to be mapped for this button click event. User can use Ctrl+Shift+${key} or Cmd+Shift+${key} as a keyboard shortcut instead of a mouse click on this button.`;
        },
      },
      variant: {
        label: 'Variant',
        type: 'select',
        options: buttonVariant,
      },
      disableElevation: {
        label: 'Disable Elevation',
        type: 'boolean',
      },
      showOnlyOnHover: {
        label: 'Show Only On Hover',
        type: 'boolean',
      },
      onClickFunction: {
        label: 'On Click',
        type: 'controllerFunction',
        category: 'Controllers',
      },
      ...toolTipProps,
    },
    isPropertyVisible(prop, itemData, { appUid, itemId, pageId }): boolean {
      if (
        itemId &&
        !isBaseColumnPropertyVisible({ appUid, platform, itemId, pageId, prop })
      ) {
        return false;
      }

      switch (prop) {
        case 'insertable':
        case 'updatable':
        case 'filterable':
        case 'sortable':
        case 'required':
        case 'viewAttribute':
          return false;
        default:
          return true;
      }
    },
    generateShortId: (item) =>
      typeof item.label === 'string' ? item.label : item.label?.value,
    getDisplayText: (item) =>
      typeof item.props.label === 'string'
        ? item.props.label
        : item.props.label.value ?? '',
    category: 'Data Grid',
    icon: { iconName: 'io-button', iconType: 'cloudio' },
    description: `**Overview**<br/>
The Button Column is an interactive column type that places a standard, clickable Button into every row of your Data Grid.
Unlike the Action Icons Column (which renders small icons), the Button Column renders full-sized buttons with text labels, variants (Contained, Outlined), and optional icons. Use this for primary row actions that need emphasis, such as "Approve," "Review," or "Add to Cart."

**1. Button Content & Appearance**<br/>
You can customize the button to match the visual hierarchy of your action.
* **Text:** The label displayed on the button (e.g., "Submit").
* **Dynamic Labels:** You can use an Expression (e.g., \`$.status === 'Pending' ? 'Approve' : 'View'\`) to change the text dynamically based on the row's data.
* **Variant:** Controls the visual style.
    * **Contained:** Solid background color (High emphasis).
    * **Outlined:** Transparent with a colored border (Medium emphasis).
    * **Text:** No border or background (Low emphasis).
* **Color:** Sets the button's theme color (e.g., Primary, Secondary, Error).
* **Icons:** Add visual cues using Start Icon (before text) or End Icon (after text).

**2. Interaction & Logic**<br/>
Buttons are useless without actions. You can bind logic directly to the click event.
* **OnClick Function:** Select a controller function to execute when the button is clicked. The function automatically receives the data of the row that was clicked.
* **TriggerOnClick:** Alternatively, add this child component to use the declarative trigger system instead of writing code.
* **Disable Condition:** A boolean expression (e.g., \`$.amount < 0\`). If true, the button in that specific row becomes greyed out and unclickable.

**3. Usability Features**<br/>
* **Full Width:** Forces the button to stretch and fill the entire width of the column cell.
* **Hot Key:** Assign a keyboard shortcut (e.g., "A"). Users can trigger the button by pressing Ctrl+Shift+A (or Cmd+Shift+A) while the row is focused.
* **Show Only On Hover:** If enabled, the button remains hidden until the user moves their mouse over the row, reducing visual clutter in dense grids.

**4. Restrictions**<br/>
* **No Sorting/Filtering:** Since this column contains buttons, not data values, you cannot sort or filter the grid based on this column.
* **No Data Binding:** It does not bind to a View Attribute in the store because it represents an action, not a stored value.`,
    helpRenders,
  };
};

export default ButtonColumnManager;
